import { Component, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZorroModule } from '../../../../modules/zorro.module';
import { BreadcrumbsComponent } from '../../../ui/admin/breadcrumbs/breadcrumbs.component';
import { ClientSelectorComponent } from '../../../ui/admin/client-selector/client-selector.component';
import { StrideAlgorithmReportFilterFormComponent } from '../../../forms/standard/stride-algorithm-report-filter-form/stride-algorithm-report-filter-form.component';
import { StrideAlgorithmReportComponent } from '../../../ui/reports/stride/stride-algorithm-report/stride-algorithm-report.component';
import { StrideLogActivityPickerComponent } from '../../../ui/reports/stride/stride-log-activity-picker/stride-log-activity-picker.component';
import { StrideLogSummaryComponent } from '../../../ui/reports/stride/stride-log-summary/stride-log-summary.component';
import { StrideReports } from '../../../../services/stride-reports';
import { Client, StrideLogActivityOption, StrideLogEntry, StrideLogStudentSummary } from '@mira-avant/mtlibs';

@Component({
	selector: 'app-stride-algorithm-report-page',
	standalone: true,
	imports: [
		CommonModule,
		ZorroModule,
		BreadcrumbsComponent,
		ClientSelectorComponent,
		StrideAlgorithmReportFilterFormComponent,
		StrideAlgorithmReportComponent,
		StrideLogActivityPickerComponent,
		StrideLogSummaryComponent,
	],
	templateUrl: './stride-algorithm-report-page.html',
	styleUrl: './stride-algorithm-report-page.scss',
})
export class StrideAlgorithmReportPage {
	public filterForm = viewChild(StrideAlgorithmReportFilterFormComponent);

	public selectedClient = signal<Client | undefined>(undefined);
	public selectedStudentUuid = signal<string | undefined>(undefined);
	public selectedActivity = signal<StrideLogActivityOption | undefined>(undefined);
	public entries = signal<StrideLogEntry[]>([]);
	public totalCount = signal<number>(0);
	public summary = signal<StrideLogStudentSummary | undefined>(undefined);

	constructor(public strideReports: StrideReports) {}

	private resetStudentSelection(): void {
		this.selectedStudentUuid.set(undefined);
		this.selectedActivity.set(undefined);
		this.entries.set([]);
		this.totalCount.set(0);
		this.summary.set(undefined);
	}

	public async onSelectClient(client: Client): Promise<void> {
		this.selectedClient.set(client);
		this.resetStudentSelection();

		// Auto-select the newest student with stride log activity so the report shows something
		// useful as soon as a client is picked.
		const result = await this.strideReports.getStrideLogStudents$(client.uuid);
		const newestStudentUuid = result.students[0]?.studentUuid;

		if (newestStudentUuid) {
			this.filterForm()?.setStudentUuid(newestStudentUuid);
			this.onStudentSelected(newestStudentUuid);
		}
	}

	public onChangeClient(): void {
		this.selectedClient.set(undefined);
		this.resetStudentSelection();
	}

	public onStudentSelected(studentUuid: string): void {
		this.selectedStudentUuid.set(studentUuid);
		this.selectedActivity.set(undefined);
		this.entries.set([]);
		this.totalCount.set(0);
		this.summary.set(undefined);
	}

	public async onActivitySelected(activity: StrideLogActivityOption): Promise<void> {
		this.selectedActivity.set(activity);

		const clientUuid = this.selectedClient()?.uuid;
		const studentUuid = this.selectedStudentUuid();
		if (!clientUuid || !studentUuid) {
			return;
		}

		const result = await this.strideReports.getStrideLogReport$({
			clientUuid,
			studentUuid,
			activityUuid: activity.activityUuid,
			take: activity.take,
		});

		this.entries.set(result.entries);
		this.totalCount.set(result.totalCount);
		this.summary.set(result.summary);
	}
}
